import { ArrowRight, Cpu, Shield, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import EditorialSection from "@/components/EditorialSection";
import OperationalFlow from "@/components/OperationalFlow";
import ProductEvidence from "@/components/ProductEvidence";
import ProductWordmark from "@/components/ProductWordmark";

const products = {
  quantumcloak: {
    name: "QuantumCloak",
    eyebrow: "Security architecture",
    headline: "Reduce exposure. Strengthen protection.",
    description: "QuantumCloak helps keep private systems out of unnecessary view and ensures access follows clear identity and policy rules across users, devices, connections and infrastructure.",
    statement: "Protection begins before a connection reaches protected infrastructure.",
    icon: Shield,
    next: { label: "Review Security & Trust", href: "/security" },
    scope: [
      ["01", "Reduce the exposed surface", "Keep private systems and services out of unnecessary view."],
      ["02", "Verify before access", "Apply identity and access rules before a connection reaches protected infrastructure."],
      ["03", "Protect trusted traffic", "Carry authorised connections through protected routes across sites, cloud and devices."],
      ["04", "Keep protection explainable", "Make current security state visible instead of asking operators to trust an unexplained indicator."],
    ],
  },
  "fpga-acceleration": {
    name: "FPGA Acceleration",
    eyebrow: "Hardware-accelerated networking",
    headline: "Accelerate performance without losing control.",
    description: "LightningFlow can move supported networking work onto dedicated FPGA acceleration when it is the best fit, while health, performance and the fallback path remain visible.",
    statement: "Acceleration is valuable when it is measurable, observable and reversible.",
    icon: Cpu,
    next: { label: "Explore the Platform", href: "/platform" },
    scope: [
      ["01", "Accelerate supported workloads", "Use dedicated hardware where it provides a practical operational benefit."],
      ["02", "Select the available path", "Use a supported hardware or software path based on suitability and availability."],
      ["03", "Observe real performance", "Track health and performance so teams can understand the benefit for each validated configuration."],
      ["04", "Retain resilient fallback", "Return to a controlled software path when acceleration is unavailable or unsuitable."],
    ],
  },
  "sheila-ai": {
    name: "Sheila AI",
    eyebrow: "Operational intelligence · mission control",
    headline: "Understand the condition, not just the alert.",
    description: "Sheila connects real LightningFlow operational context to clear explanations and practical next steps. Roles, policy and human authority remain part of the workflow.",
    statement: "Visibility without context is only observation.",
    icon: Sparkles,
    next: { label: "Explore QuantumCubeDB", href: "/quantumcubedb" },
    scope: [
      ["01", "Observe the system", "Work from current network, infrastructure, policy and service-health context."],
      ["02", "Explain the condition", "Turn technical signals into a clear description of what changed and why it matters."],
      ["03", "Recommend the next step", "Connect investigation to a practical response grounded in current operational state."],
      ["04", "Act within authority", "Respect roles, permissions and approval for high-impact change."],
    ],
  },
};

export default function ProductPage({ product }) {
  const content = products[product];
  const Icon = content.icon;
  const isSheila = product === "sheila-ai";

  return (
    <>
      <SEO title={`${content.name} — LightningFlow`} description={content.description} canonicalPath={`/${product}`} image="/platform/lightningflow-command-center-v9.png" />
      <section className="lf-hero-editorial">
        <div className="lf-container grid gap-12 py-20 md:py-28 lg:grid-cols-[1.25fr_.75fr] lg:items-end lg:py-36">
          <div><p className="lf-hero-kicker">{content.eyebrow}</p><h1 className="lf-hero-title max-w-5xl">{content.headline}</h1><p className="lf-hero-summary">{content.description}</p></div>
          {product === "quantumcloak" ? (
            <div className="border border-ink-900 bg-ink-900 p-7 text-white md:p-8">
              <ProductWordmark product="quantumcloak" className="h-auto w-full" loading="eager" />
              <p className="lf-eyebrow mt-6 text-cyan-200">{content.name}</p>
              <p className="mt-4 font-display text-3xl font-bold leading-tight">{content.statement}</p>
              <p className="mt-5 leading-relaxed text-slate-300">{content.scope[0][2]}</p>
            </div>
          ) : (
            <div className="border-t border-white/25 pt-7"><Icon className="h-9 w-9 text-cyan-200" strokeWidth={1.3} /><p className="mt-7 font-display text-3xl font-bold leading-tight">{content.statement}</p></div>
          )}
        </div>
      </section>

      <EditorialSection number="01" label={isSheila ? "Governed workflow" : "System role"} title={isSheila ? "Observe. Understand. Recommend. Act within governed authority." : "A clear role inside the wider LightningFlow system."} body={isSheila ? "Get clear explanations and practical next steps, grounded in your network. Your team keeps control of decisions and changes." : "The capability is most useful when it shares policy, topology and operational state with the rest of the platform."}>
        {isSheila ? <OperationalFlow title="Operational intelligence with authority boundaries" /> : (
          <ProductEvidence src="/platform/lightningflow-command-center-v9.webp" alt={`LightningFlow Command Centre showing ${content.name} in the wider operational platform`} caption={`${content.name} belongs inside the connected LightningFlow operating environment. Command Centre preview; interface may change.`} meta={`${content.name} · system context`} />
        )}
      </EditorialSection>

      <EditorialSection number="02" label="Operational sequence" title="See what it can do for your team." dark>
        <div className="lf-editorial-list border-white/20">
          {content.scope.map(([number, title, body]) => <article key={title} className="border-white/20"><span className="lf-list-index text-cyan-200">{number}</span><h3>{title}</h3><p className="text-slate-300">{body}</p></article>)}
        </div>
      </EditorialSection>

      {isSheila ? (
        <EditorialSection number="03" label="Mission context" title="Operational intelligence starts with an authoritative system view." body="QuantumCubeDB provides connected context across network health, events, policy and infrastructure. Sheila uses that context to help people investigate and decide.">
          <ProductEvidence src="/platform/lightningflow-command-center-v9.webp" alt="LightningFlow Command Centre showing the operational context available to Sheila AI" caption="Network activity and service health, brought into one view. Command Centre preview; interface may change." meta="Operational context for Sheila AI" />
        </EditorialSection>
      ) : null}

      <section className="lf-editorial-section"><div className="lf-container flex flex-col items-start gap-7 md:flex-row md:items-end md:justify-between"><div><p className="lf-eyebrow">Continue through the system</p><h2 className="mt-4 font-display text-4xl font-black md:text-6xl">See how the pieces work together.</h2></div><Link className="lf-btn-primary shrink-0" to={content.next.href}>{content.next.label} <ArrowRight className="h-4 w-4" /></Link></div></section>
    </>
  );
}
